import React from "react";
import {
  styled,
  Typography,
  SxProps,
  Stack,
  Box,
  AccordionSummary,
  AccordionDetails,
  Chip,
  MenuItem,
  ListItemIcon,
  Card,
  Link,
} from "@mui/material";

import SmallAccordion from "../../Misc/SmallAccordion";
import ConfigLayerContent from "./ConfigOrderContents/config-order-layer-contents";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { BasketGroupedItems, BasketTopLevelItem } from "../../../Pages/Basket/basket";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import MoreMenu from "../../Misc/MoreMenu";
import { thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { useSelector } from "react-redux";
import {
  GetBasketBundleProfiles,
  GetBasketProfiles,
  GetBasketSelectedGroupId,
  RemoveItem,
  SelectUnSelectGroupOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import { IsBasketItemMissingAsset } from "../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import { Link as RouterLink } from "react-router";
import { GetTitleRouterPath } from "../../../Utils/helpers";
import moment from "moment";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { useTranslation } from "react-i18next";

export type ConfigOrderItemCardProps = {
  className?: string;
  sx?: SxProps;
  id: number;
  group: BasketTopLevelItem;
  onClickNewConfig: (groupId: number) => void;
  onApplyToAll: (srcItem: DbOrderBasketItemInfos, items: DbOrderBasketItemInfos[]) => void;
};

const ConfigOrderItemCard: React.FC<ConfigOrderItemCardProps> = (props) => {
  const { t } = useTranslation();
  const { active: expandedAccordion, toggle: toggleAccordion, unactivate: unexpandAccordion } = useToggle(true);

  const hasContents = React.useMemo(() => props.group.items.length > 0, [props.group]);
  const selectedgroupId: number | undefined = useSelector(GetBasketSelectedGroupId);
  const dispatch = useAppDispatch();
  const profiles = useAppSelector(GetBasketProfiles);
  const bundleProfiles = useAppSelector(GetBasketBundleProfiles);

  const getItemsInGroup = (): DbOrderBasketItemInfos[] => {
    let result: DbOrderBasketItemInfos[] = [];

    props.group.items.forEach((groupLayer) => {
      groupLayer.items.forEach((item) => {
        result.push(item);
      });
    });

    return result;
  };

  const items = getItemsInGroup();

  const configurationStatus = React.useMemo(() => {
    return {
      toSetDirectives: items.filter((x) => !x.selectedProduct).length,
      toConfigure: items.filter(
        (x) => x.selectedProduct && (x.status === BasketItemStatus.New || x.status === BasketItemStatus.DirectivesDone)
      ).length,
      missingAssets: items.filter((x) => IsBasketItemMissingAsset(x, profiles, bundleProfiles).missing).length,
      complete: items.filter((x) => x.status === BasketItemStatus.OptionsDone).length,
    };
  }, [items, profiles, bundleProfiles]);

  const isCardSelected = React.useMemo(() => {
    return selectedgroupId === props.group.id;
  }, [selectedgroupId]);

  const onClickCard = React.useCallback(() => {
    if (hasContents) return;

    dispatch(SelectUnSelectGroupOnBasket(isCardSelected ? undefined : props.group.id));
  }, [selectedgroupId]);

  const handleOnClickNewConfig = React.useCallback(() => {
    unexpandAccordion();
    props.onClickNewConfig(props.group.id);
  }, [props, hasContents, unexpandAccordion]);

  const onRemove = () => {
    props.group.items.map((group) => {
      group.items.forEach((item) => {
        dispatch(RemoveItem(item.id));
      });
    });
  };

  return (
    <StyledCard variant="outlined" clickable={!hasContents} onClick={onClickCard} aria-selected={isCardSelected}>
      <CardBody direction="row" spacing={2}>
        {props.group.layer !== ObjectLayer.Group ? (
          <figure>
            <img
              src={thumbnailService.GetObjectPosterUrl(props.group.id, ThumbnailSize.small)}
              alt="Title poster"
              loading="lazy"
            />
          </figure>
        ) : (
          <FolderOutlinedIcon />
        )}
        <Stack flex={1}>
          <Typography color="text.primary" variant="subtitle1" fontWeight={600}>
            <Link
              underline="hover"
              variant="subtitle1"
              to={`${GetTitleRouterPath()}/${props.group.id}`}
              component={RouterLink}
              color="text.primary"
              sx={{ fontWeight: 500 }}
              target="_blank"
            >
              {props.group.name}
            </Link>
          </Typography>
          <Typography color="text.tier" variant="body2" mt={0.5}>
            #{props.group.id}
            {props.group.catalogType ? ` - ${props.group.catalogType}` : ""}
            {props.group.productionYear ? ` - ${moment(props.group.productionYear).year()}` : ""}
            {props.group.customerTitleId ? ` - ${props.group.customerTitleId}` : ""}
          </Typography>
        </Stack>
        <Stack spacing={1} direction="row">
          {/*<Button startIcon={<AddIcon />} disabled={false} size="small" onClick={handleOnClickNewConfig}>
            New config.
  </Button>*/}
          <MoreMenu>
            <MenuItem sx={{ color: "error.main" }} onClick={onRemove}>
              <ListItemIcon>
                <DeleteOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
              </ListItemIcon>
              {t("common:remove")}
            </MenuItem>
          </MoreMenu>
        </Stack>
      </CardBody>
      {hasContents && (
        <Box>
          <CardAccordion expanded={expandedAccordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" />}
              aria-controls="config-content"
              id="config-header"
              onClick={toggleAccordion}
            >
              <Typography fontWeight={500} color="text.primary" flex={0.58}>
                {t("common:configurations")}
              </Typography>
              <Stack ml={1.5} mr={1.5} direction="row" spacing={1.5} flex={0.42}>
                {configurationStatus.complete > 0 && (
                  <Typography color="success.main" display="inline-flex" alignItems="center">
                    {configurationStatus.complete} <CheckCircleOutlineIcon sx={{ ml: 0.5 }} />
                  </Typography>
                )}
                {configurationStatus.toConfigure > 0 && (
                  <Typography color="warning.main" display="inline-flex" alignItems="center">
                    {configurationStatus.toConfigure} <ErrorOutlineIcon sx={{ ml: 0.5 }} />
                  </Typography>
                )}
                {configurationStatus.toSetDirectives > 0 && (
                  <Typography color="text.secondary" display="inline-flex" alignItems="center">
                    {configurationStatus.toSetDirectives} <HelpOutlineIcon sx={{ ml: 0.5 }} />
                  </Typography>
                )}
                {configurationStatus.missingAssets > 0 && (
                  <Chip
                    variant="outlined"
                    color="warning"
                    label={t("chip:label.missingAssets", { count: configurationStatus.missingAssets })}
                    size="small"
                    icon={<BuildCircleOutlinedIcon />}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                {props.group.items.map((contentByLayer: BasketGroupedItems, index) => (
                  <ConfigLayerContent
                    title={contentByLayer.layer}
                    layer={contentByLayer.layer}
                    items={contentByLayer.items}
                    groupId={props.group.id}
                    onApplyToAll={props.onApplyToAll}
                    key={`contentlayer-${props.id}-${index}`}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </CardAccordion>
        </Box>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Card, { shouldForwardProp: (prop) => prop !== "clickable" })<{
  clickable: boolean;
}>(({ theme, clickable }) => ({
  position: "relative",
  overflow: "hidden",
  background: theme.palette.background.paper,

  figure: {
    width: 48,
    height: 64,
    borderRadius: 2,
    overflow: "hidden",
  },

  ...(clickable && {
    cursor: "pointer",
    transition: "background .3s",
  }),
  ...(clickable && {
    "&:not([aria-selected=true])": {
      "&:hover": {
        background: theme.palette.backgroundAlpha.hover,
      },
    },
  }),
}));

const CardBody = styled(Stack)(({ theme }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  alignItems: "center",
}));

const CardAccordion = styled(SmallAccordion)(({ theme }) => ({
  borderRadius: 0,
  background: "transparent",
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  transition: "background .3s",
  "&.Mui-expanded": {
    // background: theme.palette.backgroundAlpha.pressed,
    ".MuiAccordionSummary-expandIconWrapper": {
      transform: "rotate(0)",
    },
  },
  ".MuiAccordionSummary-root": {
    "&:hover": {
      background: theme.palette.backgroundAlpha.hover,
    },
  },
  "&:first-of-type, &:last-of-type": {
    borderRadius: 0,
  },
  ".MuiAccordionSummary-root, .MuiAccordionDetails-root": {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  ".MuiAccordionDetails-root": {
    borderTop: "none",

    paddingBottom: theme.spacing(3),
  },
  ".MuiAccordionSummary-expandIconWrapper": {
    transform: "rotate(-90deg)",
  },
}));

export default ConfigOrderItemCard;
